import { Alert, Button, Spinner } from '@recipes/ui';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth/auth-provider';
import { describeError } from '@/lib/errors';
import { AuthLayout } from './auth-layout';

type State =
  { kind: 'pending' } | { kind: 'done'; email: string } | { kind: 'error'; message: string };

const CONTINUE_AFTER_MS = 2000;

/**
 * The target of the link in the confirmation mail (docs/partial/email-and-support-spec.md 5.1).
 * The link confirms the address and nothing else: with a session the app continues by itself,
 * without one the login follows with the address filled in.
 */
export function VerifyEmailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const { status, setUser } = useAuth();
  const [state, setState] = useState<State>(
    token ? { kind: 'pending' } : { kind: 'error', message: t('auth.errors.tokenMissing') },
  );
  const [resend, setResend] = useState<'idle' | 'sending' | 'sent'>('idle');
  const attempted = useRef(false);

  useEffect(() => {
    if (!token || attempted.current || status === 'loading') return;
    attempted.current = true;
    api.auth
      .verifyEmail({ token })
      .then((user) => {
        if (status === 'authenticated') setUser(user);
        setState({ kind: 'done', email: user.email });
      })
      .catch((err) => setState({ kind: 'error', message: describeError(err) }));
  }, [token, status, setUser]);

  const signedIn = status === 'authenticated';
  useEffect(() => {
    if (state.kind !== 'done' || !signedIn) return;
    const timer = setTimeout(() => navigate('/', { replace: true }), CONTINUE_AFTER_MS);
    return () => clearTimeout(timer);
  }, [state.kind, signedIn, navigate]);

  const requestNewLink = async () => {
    setResend('sending');
    try {
      await api.auth.resendVerificationLink({ token });
    } catch {
      // The answer is the same either way – nothing about the account is revealed.
    }
    setResend('sent');
  };

  return (
    <AuthLayout title={t('auth.verifyTitle')} testId="verify-email-page">
      {state.kind === 'pending' && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground" aria-live="polite">
          <Spinner label={t('app.loading')} /> {t('auth.verifying')}
        </div>
      )}
      {state.kind === 'done' && (
        <div className="space-y-4">
          <Alert variant="success" data-testid="verify-done">
            {t('auth.verified')}
          </Alert>
          <Button asChild className="w-full">
            {signedIn ? (
              <Link to="/" replace>
                {t('auth.continue')}
              </Link>
            ) : (
              <Link to="/login" state={{ email: state.email }} data-testid="verify-to-login">
                {t('auth.login')}
              </Link>
            )}
          </Button>
        </div>
      )}
      {state.kind === 'error' && (
        <div className="space-y-4">
          <Alert variant="destructive" data-testid="verify-error">
            {state.message}
          </Alert>
          {token && resend !== 'sent' && (
            <Button
              className="w-full"
              loading={resend === 'sending'}
              onClick={() => void requestNewLink()}
              data-testid="verify-resend"
            >
              {t('auth.verifyResend')}
            </Button>
          )}
          {resend === 'sent' && (
            <Alert variant="info" data-testid="verify-resent">
              {t('auth.verifyResent')}
            </Alert>
          )}
          <Button asChild variant="outline" className="w-full">
            <Link to="/login">{t('auth.backToLogin')}</Link>
          </Button>
        </div>
      )}
    </AuthLayout>
  );
}
